import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import toast from 'react-hot-toast';
import {
  CalendarIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ConfirmationDialog,
  FolderIcon,
  ListTreeIcon,
  MapPinIcon,
  RefreshIcon,
  SearchIcon,
  ShipIcon,
  TextInput,
} from '../../components/ui';
import { formatDate } from '../../config/appConfig';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import {
  deleteSpareCase,
  deleteSpareRequisition,
  deleteSupplyCase,
  deleteSupplyRequisition,
} from '../../services/mutations';
import { normalizePaginatedListResponse, PaginatedApiResponse } from '../../services/paginatedList';
import type { SupplyCase } from '../supplies/supply-cases/useData';
import { moduleConfig as supplyCasesConfig } from '../supplies/supply-cases/config';
import type { SupplyRequisition } from '../supplies/requisitions/useData';
import { moduleConfig as supplyRequisitionsConfig } from '../supplies/requisitions/requisitionsModuleConfig';
import type { SpareCase } from '../spares/spare-cases/useData';
import { moduleConfig as spareCasesConfig } from '../spares/spare-cases/config';
import type { SpareRequisition } from '../spares/requisitions/useData';
import { moduleConfig as spareRequisitionsConfig } from '../spares/requisitions/requisitionsModuleConfig';
import {
  buildPortCallTree,
  coerceSelectionToTree,
  collectExpandKeys,
  expandedKeysForSelection,
  filterCasesForSelection,
  filterOrphanRequisitionsForSelection,
  filterTreeByQuery,
  ORPHAN_REQS_FOLDER,
  PORT_CALL_GROUP_BY_OPTIONS,
  selectionMatches,
  selectionTitle,
  type PortCallGroupBy,
  type PortCallSelection,
  type PortCallTreeIcon,
  type PortCallTreeNode,
} from './buildPortCallTree';
import {
  PortCallChildrenInbox,
  type PortCallCreateAction,
  type TaggedCase,
  type TaggedReq,
} from './PortCallChildrenInbox';
import {
  clampTreeWidth,
  loadPortCallsUiState,
  PORT_CALLS_STORAGE_COMBINED,
  PORT_CALLS_STORAGE_SPARES,
  PORT_CALLS_STORAGE_SUPPLIES,
  savePortCallsUiState,
} from './portCallsSession';
import { PurchasingModuleFilter } from '../../components/purchasing/PurchasingModuleFilter';

export type PortCallsScope = 'all' | 'supplies' | 'spares';

export type PortCallsPageProps = {
  /** Default 'all' for combined view */
  scope?: PortCallsScope;
};

type ModuleId = 'supplies' | 'spares';

const TREE_WIDTH_DEFAULT = 320;

const treeIcon = (icon: PortCallTreeIcon): React.ReactNode => {
  switch (icon) {
    case 'ship':
      return <ShipIcon className="h-3.5 w-3.5" />;
    case 'folder':
      return <FolderIcon className="h-3.5 w-3.5" />;
    case 'port':
      return <MapPinIcon className="h-3.5 w-3.5" />;
    case 'calendar':
    case 'year':
    case 'month':
      return <CalendarIcon className="h-3.5 w-3.5" />;
    default:
      return null;
  }
};

const selectionEquals = (a: PortCallSelection | null, b: PortCallSelection): boolean =>
  Boolean(a && selectionMatches(a, b));

const CountPill: React.FC<{ count: number }> = ({ count }) => (
  <span className="inline-flex min-w-[1.25rem] justify-center rounded-md bg-slate-200/80 dark:bg-slate-700/80 px-1 py-0.5 text-[10px] font-semibold tabular-nums text-slate-600 dark:text-slate-300">
    {count}
  </span>
);

const TreeRow: React.FC<{
  indent: number;
  selected: boolean;
  expandable?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  onSelect: () => void;
  label: string;
  count: number;
  muted?: boolean;
  icon?: React.ReactNode;
  iconClassName?: string;
}> = ({
  indent,
  selected,
  expandable,
  expanded,
  onToggle,
  onSelect,
  label,
  count,
  muted,
  icon,
  iconClassName,
}) => {
  return (
    <div
      className={cn(
        'group flex items-center gap-0.5 pr-2 py-1 rounded-md cursor-pointer text-sm',
        selected
          ? 'bg-sky-100 text-sky-900 dark:bg-sky-950/60 dark:text-sky-100'
          : 'hover:bg-slate-100 dark:hover:bg-slate-800/80 text-slate-800 dark:text-slate-200',
      )}
      style={{ paddingLeft: 6 + indent * 12 }}
      onClick={onSelect}
      role="treeitem"
      aria-selected={selected}
    >
      {expandable ? (
        <button
          type="button"
          className={cn(
            'shrink-0 h-6 w-6 inline-flex items-center justify-center rounded',
            'text-slate-500 hover:bg-slate-200/80 dark:hover:bg-slate-700',
          )}
          aria-label={expanded ? 'Collapse' : 'Expand'}
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.();
          }}
        >
          {expanded ? <ChevronDownIcon className="h-3.5 w-3.5" /> : <ChevronRightIcon className="h-3.5 w-3.5" />}
        </button>
      ) : (
        <span className="shrink-0 w-6" />
      )}
      {icon && (
        <span
          className={cn(
            'shrink-0 inline-flex items-center justify-center mr-2',
            selected ? 'text-sky-700 dark:text-sky-300' : iconClassName ?? 'text-slate-400 dark:text-slate-500',
          )}
          aria-hidden
        >
          {icon}
        </span>
      )}
      <span
        className={cn(
          'min-w-0 flex-1 truncate',
          muted && 'italic text-slate-500 dark:text-slate-400',
          selected && 'font-medium',
        )}
        title={label}
      >
        {label}
      </span>
      <CountPill count={count} />
    </div>
  );
};

const TreeNodes: React.FC<{
  nodes: PortCallTreeNode[];
  depth: number;
  selection: PortCallSelection | null;
  onSelect: (sel: PortCallSelection) => void;
  expanded: Set<string>;
  toggle: (key: string) => void;
  forceExpand: boolean;
}> = ({ nodes, depth, selection, onSelect, expanded, toggle, forceExpand }) => (
  <>
    {nodes.map((node) => {
      const hasChildren = Boolean(node.children?.length);
      const expKey = node.expandKey;
      const isExpanded = Boolean(expKey && (expanded.has(expKey) || forceExpand));
      return (
        <div key={node.key}>
          <TreeRow
            indent={depth}
            selected={selectionEquals(selection, node.selection)}
            expandable={hasChildren}
            expanded={isExpanded}
            onToggle={expKey ? () => toggle(expKey) : undefined}
            onSelect={() => onSelect(node.selection)}
            label={node.label}
            count={node.count}
            muted={node.muted}
            icon={treeIcon(node.icon)}
            iconClassName={node.iconClassName}
          />
          {hasChildren && isExpanded && (
            <TreeNodes
              nodes={node.children!}
              depth={depth + 1}
              selection={selection}
              onSelect={onSelect}
              expanded={expanded}
              toggle={toggle}
              forceExpand={forceExpand}
            />
          )}
        </div>
      );
    })}
  </>
);

const PortCallTree: React.FC<{
  tree: PortCallTreeNode[];
  selection: PortCallSelection | null;
  onSelect: (sel: PortCallSelection) => void;
  query: string;
  expanded: Set<string>;
  onExpandedChange: React.Dispatch<React.SetStateAction<Set<string>>>;
}> = ({ tree, selection, onSelect, query, expanded, onExpandedChange }) => {
  useEffect(() => {
    onExpandedChange((prev) => {
      const next = new Set(prev);
      Array.from(expandedKeysForSelection(selection)).forEach((k) => next.add(k));
      return next;
    });
  }, [selection, onExpandedChange]);

  const q = query.trim();
  const visible = useMemo(() => filterTreeByQuery(tree, q), [tree, q]);

  useEffect(() => {
    if (!q) return;
    onExpandedChange((prev) => {
      const next = new Set(prev);
      collectExpandKeys(visible).forEach((k) => next.add(k));
      return next;
    });
  }, [q, visible, onExpandedChange]);

  const toggle = (key: string) => {
    onExpandedChange((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (visible.length === 0) {
    return (
      <div className="px-3 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
        No matching vessels or ports
      </div>
    );
  }

  return (
    <div role="tree" className="py-1">
      <TreeNodes
        nodes={visible}
        depth={0}
        selection={selection}
        onSelect={onSelect}
        expanded={expanded}
        toggle={toggle}
        forceExpand={Boolean(q)}
      />
    </div>
  );
};

export const PortCallsPage: React.FC<PortCallsPageProps> = ({ scope = 'all' }) => {
  const { colors } = useTheme();
  const { can } = useAuth();

  const PORT_CALLS_PATH =
    scope === 'supplies' ? '/supplies/port-calls'
    : scope === 'spares' ? '/spares/port-calls'
    : '/port-calls';

  const STORAGE_KEY =
    scope === 'supplies' ? PORT_CALLS_STORAGE_SUPPLIES
    : scope === 'spares' ? PORT_CALLS_STORAGE_SPARES
    : PORT_CALLS_STORAGE_COMBINED;

  const canViewSupplies = scope !== 'spares' && can('supply_cases', 'view');
  const canViewSpares = scope !== 'supplies' && can('spare_cases', 'view');

  const [boot] = useState(() => loadPortCallsUiState(STORAGE_KEY));
  const [treeQuery, setTreeQuery] = useState(boot?.treeQuery ?? '');
  const [groupBy, setGroupBy] = useState<PortCallGroupBy>(
    () => boot?.groupBy ?? 'vessel-date-port',
  );
  const [selection, setSelection] = useState<PortCallSelection | null>(boot?.selection ?? null);
  const [expanded, setExpanded] = useState(() => new Set(boot?.expandedKeys ?? []));
  const [treeWidth, setTreeWidth] = useState(() =>
    clampTreeWidth(boot?.treeWidth ?? TREE_WIDTH_DEFAULT),
  );
  const [treeResizing, setTreeResizing] = useState(false);
  const treeResizeStartRef = useRef({ x: 0, width: TREE_WIDTH_DEFAULT });
  const [groupByMenuOpen, setGroupByMenuOpen] = useState(false);
  const groupByMenuRef = useRef<HTMLDivElement>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<
    { type: 'case' | 'requisition'; id: number; moduleId: ModuleId } | null
  >(null);
  const [deleting, setDeleting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [moduleFilter, setModuleFilter] = useState<'all' | ModuleId>('all');

  const showingOrphanReqs = selection?.folder === ORPHAN_REQS_FOLDER;

  /** Which module's config drives new-case route when only one module is in view. */
  const effectiveModule: ModuleId =
    scope === 'supplies' ? 'supplies'
    : scope === 'spares' ? 'spares'
    : moduleFilter !== 'all' ? moduleFilter
    : 'supplies';

  const activeCasesConfig = effectiveModule === 'spares' ? spareCasesConfig : supplyCasesConfig;
  const activeRequisitionsConfig = effectiveModule === 'spares' ? spareRequisitionsConfig : supplyRequisitionsConfig;

  // No page/limit → backend returns full list (needed for the tree). Null keys are skipped by SWR.
  const suppliesCasesUrl = canViewSupplies ? '/supply-cases?sortKey=eta&sortDirection=desc' : null;
  const suppliesOrphanReqsUrl = canViewSupplies
    ? '/supply-requisitions?sortKey=reqDate&sortDirection=desc&filters='
      + encodeURIComponent(JSON.stringify({ filters: { noCase: ['true'] } }))
    : null;
  const sparesCasesUrl = canViewSpares ? '/spare-cases?sortKey=eta&sortDirection=desc' : null;
  const sparesOrphanReqsUrl = canViewSpares
    ? '/spare-requisitions?sortKey=reqDate&sortDirection=desc&filters='
      + encodeURIComponent(JSON.stringify({ filters: { noCase: ['true'] } }))
    : null;

  const {
    data: suppliesCasesData,
    error: suppliesCasesError,
    isLoading: suppliesCasesLoading,
    mutate: mutateSuppliesCases,
  } = useSWR<PaginatedApiResponse<SupplyCase> | SupplyCase[]>(
    suppliesCasesUrl,
    fetcher,
    { revalidateOnFocus: true, revalidateOnMount: true },
  );
  const {
    data: suppliesOrphanData,
    error: suppliesOrphanError,
    isLoading: suppliesOrphanLoading,
    mutate: mutateSuppliesOrphans,
  } = useSWR<PaginatedApiResponse<SupplyRequisition> | SupplyRequisition[]>(
    suppliesOrphanReqsUrl,
    fetcher,
    { revalidateOnFocus: true, revalidateOnMount: true },
  );
  const {
    data: sparesCasesData,
    error: sparesCasesError,
    isLoading: sparesCasesLoading,
    mutate: mutateSparesCases,
  } = useSWR<PaginatedApiResponse<SpareCase> | SpareCase[]>(
    sparesCasesUrl,
    fetcher,
    { revalidateOnFocus: true, revalidateOnMount: true },
  );
  const {
    data: sparesOrphanData,
    error: sparesOrphanError,
    isLoading: sparesOrphanLoading,
    mutate: mutateSparesOrphans,
  } = useSWR<PaginatedApiResponse<SpareRequisition> | SpareRequisition[]>(
    sparesOrphanReqsUrl,
    fetcher,
    { revalidateOnFocus: true, revalidateOnMount: true },
  );

  const casesLoading = suppliesCasesLoading || sparesCasesLoading;
  const casesError = suppliesCasesError || sparesCasesError;
  const orphanLoading = suppliesOrphanLoading || sparesOrphanLoading;
  const orphanError = suppliesOrphanError || sparesOrphanError;
  const isLoading = casesLoading || orphanLoading;
  const error = casesError || orphanError;

  // Revalidate when returning to this page (fresh table data under the restored selection).
  useEffect(() => {
    void mutateSuppliesCases();
    void mutateSuppliesOrphans();
    void mutateSparesCases();
    void mutateSparesOrphans();
  }, [mutateSuppliesCases, mutateSuppliesOrphans, mutateSparesCases, mutateSparesOrphans]);

  useEffect(() => {
    savePortCallsUiState({
      selection,
      expandedKeys: Array.from(expanded),
      treeQuery,
      treeWidth,
      groupBy,
    }, STORAGE_KEY);
  }, [selection, expanded, treeQuery, treeWidth, groupBy, STORAGE_KEY]);

  useEffect(() => {
    if (!groupByMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!groupByMenuRef.current?.contains(e.target as Node)) {
        setGroupByMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setGroupByMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [groupByMenuOpen]);

  useEffect(() => {
    if (!treeResizing) return;
    const onMove = (e: MouseEvent) => {
      const start = treeResizeStartRef.current;
      setTreeWidth(clampTreeWidth(start.width + (e.clientX - start.x)));
    };
    const onUp = () => setTreeResizing(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [treeResizing]);

  const persistUiNow = useCallback(() => {
    savePortCallsUiState({
      selection,
      expandedKeys: Array.from(expanded),
      treeQuery,
      treeWidth,
      groupBy,
    }, STORAGE_KEY);
  }, [selection, expanded, treeQuery, treeWidth, groupBy, STORAGE_KEY]);

  const suppliesCases = useMemo<TaggedCase[]>(() => {
    const list = Array.isArray(suppliesCasesData)
      ? suppliesCasesData
      : normalizePaginatedListResponse<SupplyCase>(suppliesCasesData, 1, 10_000).data;
    return list.map((c) => ({ ...c, moduleId: 'supplies' as const }));
  }, [suppliesCasesData]);

  const sparesCases = useMemo<TaggedCase[]>(() => {
    const list = Array.isArray(sparesCasesData)
      ? sparesCasesData
      : normalizePaginatedListResponse<SpareCase>(sparesCasesData, 1, 10_000).data;
    return list.map((c) => ({ ...c, moduleId: 'spares' as const }));
  }, [sparesCasesData]);

  const suppliesOrphanReqs = useMemo<TaggedReq[]>(() => {
    const list = Array.isArray(suppliesOrphanData)
      ? suppliesOrphanData
      : normalizePaginatedListResponse<SupplyRequisition>(suppliesOrphanData, 1, 10_000).data;
    return list.map((r) => ({ ...r, moduleId: 'supplies' as const }));
  }, [suppliesOrphanData]);

  const sparesOrphanReqs = useMemo<TaggedReq[]>(() => {
    const list = Array.isArray(sparesOrphanData)
      ? sparesOrphanData
      : normalizePaginatedListResponse<SpareRequisition>(sparesOrphanData, 1, 10_000).data;
    return list.map((r) => ({ ...r, moduleId: 'spares' as const }));
  }, [sparesOrphanData]);

  const allCases = useMemo<TaggedCase[]>(
    () => [...suppliesCases, ...sparesCases],
    [suppliesCases, sparesCases],
  );
  const allOrphanReqs = useMemo<TaggedReq[]>(
    () => [...suppliesOrphanReqs, ...sparesOrphanReqs],
    [suppliesOrphanReqs, sparesOrphanReqs],
  );

  const moduleCounts = useMemo(
    () => ({
      all: allCases.length,
      supplies: allCases.filter((c) => c.moduleId === 'supplies').length,
      spares: allCases.filter((c) => c.moduleId === 'spares').length,
    }),
    [allCases],
  );

  // Module filter chips (combined view only) narrow the tagged lists before the tree is built.
  const cases = useMemo<TaggedCase[]>(() => {
    if (scope !== 'all' || moduleFilter === 'all') return allCases;
    return allCases.filter((c) => c.moduleId === moduleFilter);
  }, [allCases, moduleFilter, scope]);

  const orphanRequisitions = useMemo<TaggedReq[]>(() => {
    if (scope !== 'all' || moduleFilter === 'all') return allOrphanReqs;
    return allOrphanReqs.filter((r) => r.moduleId === moduleFilter);
  }, [allOrphanReqs, moduleFilter, scope]);

  const tree = useMemo(
    () => buildPortCallTree(cases, orphanRequisitions, groupBy, formatDate),
    [cases, orphanRequisitions, groupBy],
  );

  const selectionCases = useMemo(
    () => filterCasesForSelection(cases, selection),
    [cases, selection],
  );

  const selectionOrphanReqs = useMemo(
    () => filterOrphanRequisitionsForSelection(orphanRequisitions, selection),
    [orphanRequisitions, selection],
  );

  const title = useMemo(
    () => selectionTitle(tree, selection),
    [tree, selection],
  );

  useEffect(() => {
    const next = coerceSelectionToTree(tree, selection);
    if (!next) {
      if (selection != null) setSelection(null);
      return;
    }
    if (!selectionEquals(selection, next)) setSelection(next);
  }, [selection, tree]);

  const onGroupByChange = useCallback((next: PortCallGroupBy) => {
    setGroupBy(next);
    setExpanded(new Set());
    setSelection((prev) => {
      if (!prev) return null;
      if (prev.vesselKey) return { vesselKey: prev.vesselKey };
      if (prev.portKey) return { portKey: prev.portKey };
      if (prev.dateKey) return { dateKey: prev.dateKey };
      return null;
    });
  }, []);

  const refreshAll = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        mutateSuppliesCases(),
        mutateSuppliesOrphans(),
        mutateSparesCases(),
        mutateSparesOrphans(),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  }, [mutateSuppliesCases, mutateSuppliesOrphans, mutateSparesCases, mutateSparesOrphans]);

  const handleDelete = useCallback(async () => {
    if (!deleteConfirm) return;
    const isSpares = deleteConfirm.moduleId === 'spares';
    setDeleting(true);
    try {
      if (deleteConfirm.type === 'case') {
        if (isSpares) {
          await deleteSpareCase(deleteConfirm.id);
          toast.success('Spare case deleted');
          await mutateSparesCases();
        } else {
          await deleteSupplyCase(deleteConfirm.id);
          toast.success('Supply case deleted');
          await mutateSuppliesCases();
        }
      } else if (isSpares) {
        await deleteSpareRequisition(deleteConfirm.id);
        toast.success('Requisition deleted');
        await mutateSparesOrphans();
      } else {
        await deleteSupplyRequisition(deleteConfirm.id);
        toast.success('Requisition deleted');
        await mutateSuppliesOrphans();
      }
      setDeleteConfirm(null);
    } catch {
      const cfg =
        deleteConfirm.type === 'requisition'
          ? (isSpares ? spareRequisitionsConfig : supplyRequisitionsConfig)
          : (isSpares ? spareCasesConfig : supplyCasesConfig);
      toast.error(cfg.delete.errorMessage);
    } finally {
      setDeleting(false);
    }
  }, [deleteConfirm, mutateSuppliesCases, mutateSuppliesOrphans, mutateSparesCases, mutateSparesOrphans]);

  const vesselIdFromSelection = selection?.vesselKey?.startsWith('v:') ? selection.vesselKey.slice(2) : '';

  const buildRequisitionNewHref = useCallback(
    (cfg: typeof supplyRequisitionsConfig | typeof spareRequisitionsConfig) => {
      const qs = new URLSearchParams({ noCase: '1' });
      if (vesselIdFromSelection) qs.set('vesselId', vesselIdFromSelection);
      return `${cfg.module.route.new}?${qs.toString()}`;
    },
    [vesselIdFromSelection],
  );

  const createActions = useMemo((): PortCallCreateAction[] => {
    if (showingOrphanReqs) {
      if (scope === 'all') {
        const actions: PortCallCreateAction[] = [];
        if (canViewSupplies) {
          actions.push({
            href: buildRequisitionNewHref(supplyRequisitionsConfig),
            label: 'New supply requisition',
            primary: true,
          });
        }
        if (canViewSpares) {
          actions.push({
            href: buildRequisitionNewHref(spareRequisitionsConfig),
            label: 'New spare requisition',
            primary: actions.length === 0,
          });
        }
        return actions;
      }
      return [
        {
          href: buildRequisitionNewHref(activeRequisitionsConfig),
          label: 'New requisition',
          primary: true,
        },
      ];
    }
    if (scope === 'all') {
      const actions: PortCallCreateAction[] = [];
      if (canViewSupplies) {
        actions.push({
          href: supplyCasesConfig.module.route.new,
          label: 'New supply case',
          primary: true,
        });
      }
      if (canViewSpares) {
        actions.push({
          href: spareCasesConfig.module.route.new,
          label: 'New spare case',
          primary: actions.length === 0,
        });
      }
      return actions;
    }
    return [
      {
        href: activeCasesConfig.module.route.new,
        label: 'New case',
        primary: true,
      },
    ];
  }, [
    showingOrphanReqs,
    scope,
    canViewSupplies,
    canViewSpares,
    buildRequisitionNewHref,
    activeRequisitionsConfig,
    activeCasesConfig,
  ]);

  const deleteDialogConfig = deleteConfirm
    ? deleteConfirm.type === 'requisition'
      ? (deleteConfirm.moduleId === 'spares' ? spareRequisitionsConfig : supplyRequisitionsConfig)
      : (deleteConfirm.moduleId === 'spares' ? spareCasesConfig : supplyCasesConfig)
    : null;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="mb-1 flex min-w-0 flex-shrink-0 flex-row items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-row items-center gap-2">
          {scope === 'all' && (
            <PurchasingModuleFilter
              value={moduleFilter}
              onChange={setModuleFilter}
              counts={moduleCounts}
              showSupplies={canViewSupplies}
              showSpares={canViewSpares}
              className="shrink-0"
            />
          )}
        </div>
        <div
          className={cn(
            'flex flex-shrink-0 flex-nowrap items-center gap-2 rounded-xl border py-1.5 pl-3 pr-3',
            colors.border,
            colors.surface,
          )}
        >
          <button
            type="button"
            onClick={() => {
              void refreshAll();
            }}
            disabled={isRefreshing}
            className={cn(
              'inline-flex h-9 w-9 items-center justify-center rounded-lg border transition-colors',
              colors.surface,
              colors.text,
              colors.border,
              colors.interactiveHover,
              isRefreshing && 'opacity-60',
            )}
            title="Refresh"
            aria-label="Refresh"
          >
            <RefreshIcon className={cn('h-5 w-5', isRefreshing && 'animate-spin')} />
          </button>
        </div>
      </div>

      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border shadow-sm',
          'bg-white dark:bg-slate-950',
          colors.border,
        )}
      >
        <div
          className="flex min-h-0 flex-1 flex-col lg:flex-row"
          style={{ ['--pc-tree-w' as string]: `${treeWidth}px` }}
        >
          <aside
            className={cn(
              'flex max-h-64 w-full min-h-0 flex-col border-b lg:max-h-none lg:w-[var(--pc-tree-w)] lg:shrink-0 lg:border-b-0',
              colors.border,
            )}
          >
            <div className="flex-shrink-0 border-b border-slate-100 p-2 dark:border-slate-800">
              <div className="flex items-center gap-1.5">
                <div className="relative shrink-0" ref={groupByMenuRef}>
                  <button
                    type="button"
                    onClick={() => setGroupByMenuOpen((o) => !o)}
                    className={cn(
                      'inline-flex h-9 w-9 items-center justify-center rounded-lg border transition-colors',
                      colors.border,
                      colors.surface,
                      colors.text,
                      colors.interactiveHover,
                      groupByMenuOpen &&
                        'border-sky-400 bg-sky-50 text-sky-700 dark:border-sky-600 dark:bg-sky-950/50 dark:text-sky-200',
                    )}
                    title="Group by"
                    aria-label="Group by"
                    aria-haspopup="menu"
                    aria-expanded={groupByMenuOpen}
                  >
                    <ListTreeIcon className="h-4 w-4" />
                  </button>
                  {groupByMenuOpen && (
                    <div
                      role="menu"
                      aria-label="Tree grouping"
                      className={cn(
                        'absolute left-0 top-[calc(100%+6px)] z-50 w-[min(18.5rem,calc(100vw-2rem))]',
                        'overflow-hidden rounded-xl border shadow-xl',
                        'bg-white dark:bg-slate-900',
                        colors.border,
                      )}
                    >
                      <div className={cn('border-b px-3 py-2.5', colors.border)}>
                        <p className={cn('text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>
                          Group by
                        </p>
                        <p className={cn('mt-0.5 text-xs', colors.textMuted)}>
                          Choose how the tree is organized
                        </p>
                      </div>
                      <div className="dashboard-scrollbar max-h-72 overflow-y-auto p-1.5">
                        {PORT_CALL_GROUP_BY_OPTIONS.map((opt) => {
                          const active = groupBy === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              role="menuitemradio"
                              aria-checked={active}
                              onClick={() => {
                                onGroupByChange(opt.value);
                                setGroupByMenuOpen(false);
                              }}
                              className={cn(
                                'flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors',
                                active
                                  ? 'bg-sky-50 text-sky-900 dark:bg-sky-950/60 dark:text-sky-100'
                                  : cn(colors.text, 'hover:bg-slate-100 dark:hover:bg-slate-800/80'),
                              )}
                            >
                              <span
                                className={cn(
                                  'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                                  active
                                    ? 'border-sky-500 bg-sky-500 text-white'
                                    : 'border-slate-300 dark:border-slate-600',
                                )}
                              >
                                {active && <CheckIcon className="h-2.5 w-2.5" />}
                              </span>
                              <span className="min-w-0 flex-1">
                                <span
                                  className={cn(
                                    'block text-sm font-medium leading-snug',
                                    active && 'font-semibold',
                                  )}
                                >
                                  {opt.label}
                                </span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <div className="relative min-w-0 flex-1">
                  <SearchIcon className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                  <TextInput
                    value={treeQuery}
                    onChange={(e) => setTreeQuery(e.target.value)}
                    placeholder="Filter tree…"
                    className="h-9 pl-8 text-sm"
                  />
                </div>
              </div>
            </div>
            <div className="dashboard-scrollbar flex-1 overflow-y-auto px-1">
              {isLoading ? (
                <div className="px-3 py-8 text-center text-sm text-slate-500">Loading…</div>
              ) : error ? (
                <div className="px-3 py-8 text-center text-sm text-rose-600">Could not load data</div>
              ) : (
                <PortCallTree
                  tree={tree}
                  selection={selection}
                  onSelect={setSelection}
                  query={treeQuery}
                  expanded={expanded}
                  onExpandedChange={setExpanded}
                />
              )}
            </div>
          </aside>

          <div
            role="separator"
            aria-orientation="vertical"
            aria-valuenow={treeWidth}
            aria-label="Resize tree panel"
            title="Drag to resize"
            onMouseDown={(e) => {
              if (e.button !== 0) return;
              e.preventDefault();
              treeResizeStartRef.current = { x: e.clientX, width: treeWidth };
              setTreeResizing(true);
            }}
            className={cn(
              'group hidden w-1.5 shrink-0 cursor-col-resize items-center justify-center lg:flex',
              'hover:bg-indigo-500/20 active:bg-indigo-500/30',
              'border-l',
              colors.border,
              treeResizing && 'bg-indigo-500/30',
            )}
          >
            <div className="h-12 w-0.5 rounded-full bg-slate-400 opacity-60 group-hover:opacity-100 dark:bg-slate-500" />
          </div>

          <section className="flex min-h-0 min-w-0 flex-1 flex-col p-2 sm:p-3">
            {!selection ? (
              <div className="flex flex-1 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900/50">
                <div className="w-full max-w-sm px-6 py-12 text-center">
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100">Select a call</p>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Pick a node on the left to see its cases.
                  </p>
                </div>
              </div>
            ) : (
              <PortCallChildrenInbox
                title={title}
                mode={showingOrphanReqs ? 'requisitions' : 'cases'}
                scope={scope}
                cases={selectionCases as TaggedCase[]}
                requisitions={selectionOrphanReqs as TaggedReq[]}
                isLoading={showingOrphanReqs ? orphanLoading : casesLoading}
                portCallsPath={PORT_CALLS_PATH}
                createActions={createActions}
                onPersistUi={persistUiNow}
                onDeleteCase={(id, moduleId) => setDeleteConfirm({ type: 'case', id, moduleId })}
                onDeleteRequisition={(id, moduleId) =>
                  setDeleteConfirm({ type: 'requisition', id, moduleId })
                }
              />
            )}
          </section>
        </div>
      </div>

      <ConfirmationDialog
        isOpen={deleteConfirm != null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => {
          void handleDelete();
        }}
        title={deleteDialogConfig?.delete.title ?? ''}
        message={deleteDialogConfig?.delete.message ?? ''}
        confirmText={deleteDialogConfig?.delete.confirmText ?? 'Delete'}
        cancelText={deleteDialogConfig?.delete.cancelText ?? 'Cancel'}
        isLoading={deleting}
      />
    </div>
  );
};

export default PortCallsPage;
